import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Helmet } from "react-helmet";

import { userRoleOptions, userRoleNames } from "@ats/src/lib/lookups";
import { useAddActiveUserGate } from "@shared/hooks/useAddActiveUserGate";

import InviteListItem from "@ats/src/views/accountAdmin/components/InviteListItem";
import MemberListItem from "@ats/src/views/accountAdmin/components/MemberListItem";

import { useToastContext } from "@shared/context/ToastContext";
import { useModalContext } from "@shared/context/ModalContext";

import InviteModal from "@ats/src/components/modals/InviteModal";
import ConfirmTransferOwnershipModal from "@ats/src/components/modals/ConfirmTransferOwnershipModal";
import UpgradeUserLimitModal from "@ats/src/components/modals/UpgradeUserLimitModal";
import ApiKeyRevokeWarningModal from "@ats/src/components/modals/ApiKeyRevokeWarningModal";
import StartFreeTrialModal from "@ats/src/components/modals/StartFreeTrialModal";
import RestartSubscriptionUserLimitModal from "@ats/src/components/modals/RestartSubscriptionUserLimitModal";
import SettingsContainer from "@ats/src/components/shared/SettingsContainer";
import FormSection from "@ats/src/components/forms/FormSection";
import Button from "@ats/src/components/shared/Button";

import {
  useAllInvites,
  useUpdateInvite,
  useResendInvite,
  useDestroyInvite,
} from "@shared/queryHooks/useInvite";

import { useUpdateUser, useDeactivateUser } from "@shared/queryHooks/useUser";
import {
  useUpdateOrganizationUser,
  useDeactivateOrganizationUser,
} from "@shared/queryHooks/useOrganizationUser";
import { useTransferOrganizationOwnership } from "@shared/queryHooks/useOrganization";
import { trackEvent } from "@shared/lib/posthog";

function AccountTeam(props) {
  const { currentUser, currentOrganization } = props;
  const currentUserId = currentUser?.id;
  const addToast = useToastContext();
  const { openModal, removeModal } = useModalContext();

  // Add user limit gate hook
  const { checkGate, isLoading: isLoadingUserGate } = useAddActiveUserGate();

  const [state, setState] = React.useState({
    currentOwnerId: currentOrganization?.ownerId,
    currentUserRole: currentUser?.currentOrganizationUser?.role,
    isCurrentOwner: currentUser?.currentOrganizationUser?.role === "org_owner",
  });
  const { currentOwnerId, currentUserRole, isCurrentOwner } = state;

  /* Queries
  --===================================================-- */
  const { data: invitesData, isLoading: isLoadingInvites } = useAllInvites();
  const { mutate: updateInvite, isLoading: isLoadingUpdateInvite } = useUpdateInvite();
  const { mutate: resendInvite, isLoading: isLoadingResendInvite } = useResendInvite();
  const { mutate: destroyInvite, isLoading: isLoadingDestroyInvite } = useDestroyInvite();
  // const { data: userData, isLoading: isLoadingUser } = useGetUser();
  const { mutate: updateUser, isLoading: isLoadingUpdateUser } = useUpdateUser();
  const {
    mutate: updateOrganizationUser,
    isLoading: isLoadingUpdateOrganizationUser,
  } = useUpdateOrganizationUser();
  const { mutate: deactivateUser, isLoading: isLoadingDeactivateUser } = useDeactivateUser();
  const {
    mutate: deactivateOrganizationUser,
    isLoading: isLoadingDeactivateOrganizationUser,
  } = useDeactivateOrganizationUser();
  const {
    mutate: transferOwnership,
    isLoading: isLoadingTransferOwnership,
  } = useTransferOrganizationOwnership();

  window.logger("%c[AccountTeam] RENDER", "background-color: #7db4ec", {
    invitesData,
    isLoadingInvites,
    currentUser,
    currentOrganization,
    state,
  });

  const inviteNodes = () => {
    if (invitesData == undefined) {
      return [];
    }

    return invitesData.items.map((invite) => {
      return (
        <InviteListItem
          key={invite.id}
          invite={invite}
          roles={userRoleOptions}
          resendInvite={handleResendInvite}
          destroyInvite={handleDestroyInvite}
          updateInviteRole={updateInviteRole}
          currentUser={currentUser}
        />
      );
    });
  };

  const memberNodes = () => {
    return currentOrganization.organizationUsers.map((organizationUser, index) => {
      return (
        <MemberListItem
          key={organizationUser.id}
          organizationUser={organizationUser}
          roles={userRoleOptions}
          updateOrganizationUserRole={handleUpdateOrganizationUserRole}
          deactivateOrganizationUser={handleDeactivateOrganizationUser}
          activateOrganizationUser={handleActivateOrganizationUser}
          transferOrgOwnership={handleClickTransferOwnership}
          isSelf={currentUserId === organizationUser.user.id}
          isOrgOwner={organizationUser.user.id === currentOwnerId}
          currentUser={currentUser}
          isCurrentOwner={isCurrentOwner}
          currentUserRole={currentUserRole}
        />
      );
    });
  };

  const handleUpdateOrganizationUserRole = ({ organizationUserId, role }) => {
    updateOrganizationUser(
      { id: organizationUserId, role },
      {
        onSuccess: (data) => {
          addToast({
            title: `Updated ${data.user.fullName}'s role to ${userRoleNames(data.role)}`,
            kind: "success",
          });
        },
        onError: (response: any) => {
          if (response.data.errors.general) {
            addToast({ title: response.data.errors.general[0], kind: "warning" });
          }
        },
      },
    );
  };

  const handleDeactivateOrganizationUser = (organizationUserId) => {
    const organizationUser = currentOrganization.organizationUsers.find(
      (ou) => ou.id === organizationUserId,
    );

    if (organizationUser.hasActiveApiKey) {
      const modal = (
        <ApiKeyRevokeWarningModal
          userName={organizationUser.user.fullName}
          actionType="deactivate"
          onConfirm={() => {
            removeModal();
            performDeactivation(organizationUserId);
          }}
          onCancel={removeModal}
        />
      );
      openModal(modal);
    } else {
      performDeactivation(organizationUserId);
    }
  };

  const performDeactivation = (organizationUserId: number) => {
    deactivateOrganizationUser(
      { id: organizationUserId },
      {
        onSuccess: (data) => {
          addToast({
            title: `Successfully deactivated ${data.user.fullName}`,
            kind: "success",
          });
        },
        onError: (response: any) => {
          if (response.data.errors.general) {
            addToast({ title: response.data.errors.general[0], kind: "warning" });
          }
        },
      },
    );
  };

  const handleActivateOrganizationUser = (organizationUserId) => {
    trackEvent("reactivate_member_clicked");
    const result = checkGate();

    if (!result.shouldProceed) {
      if (result.errorMessage) {
        // Handle error cases with user feedback
        addToast({
          title: result.errorMessage,
          kind: "warning",
          delay: 5000,
        });
        return;
      }
    }

    if (result.shouldProceed) {
      updateOrganizationUser(
        { id: organizationUserId, isActive: true },
        {
          onSuccess: (data) => {
            trackEvent("member_reactivated");
            addToast({
              title: `Successfully re-activated ${data.user.fullName}`,
              kind: "success",
            });
          },
          onError: (response: any) => {
            if (response.data.errors.general) {
              addToast({ title: response.data.errors.general[0], kind: "warning", delay: 10000 });
            }
          },
        },
      );
    } else if (result.modalType === "upgrade") {
      // Handle upgrade modal for user limit
      trackEvent("reactivate_member_upgrade_modal_shown", { current_plan: result.modalData!.currentPlanName! });
      const modal = (
        <UpgradeUserLimitModal
          onCancel={() => removeModal()}
          currentPlanName={result.modalData!.currentPlanName!}
          userLimit={result.modalData!.userLimit!}
          activeUserCount={result.modalData!.activeUserCount!}
          nextPlanName={result.modalData!.nextPlanName}
          hasPendingInvites={result.modalData!.hasPendingInvites}
          hasMultipleActiveUsers={result.modalData!.hasMultipleActiveUsers}
          location={props.location}
          history={props.history}
        />
      );
      openModal(modal);
    } else if (result.modalType === "freeTrial") {
      const modal = (
        <StartFreeTrialModal
          onCancel={() => removeModal()}
          purpose="addTeamMember"
          location={props.location}
        />
      );
      openModal(modal);
    } else if (result.modalType === "reactivate") {
      const modal = (
        <RestartSubscriptionUserLimitModal
          onCancel={() => removeModal()}
          location={props.location}
          history={props.history}
        />
      );
      openModal(modal);
    }
  };

  const handleClickTransferOwnership = (organizationUserId) => {
    const modal = (
      <ConfirmTransferOwnershipModal
        onConfirm={() => {
          removeModal();
          handleTranferOwnership(organizationUserId);
        }}
        onCancel={() => {
          removeModal();
        }}
      />
    );

    openModal(modal);
  };

  const handleTranferOwnership = (organizationUserId) => {
    window.logger("%c[AccountTeam] handleTransferOwnership", "background-color: #7db4ec", {
      organizationUserId,
    });

    transferOwnership(
      { id: currentOrganization.id, organizationUserId: organizationUserId },
      {
        onSuccess: (data) => {
          window.logger("%c[AccountTeam] handleTransferOwnership", "background-color: #7db4ec", {
            data,
          });
          setState({
            currentOwnerId: data?.ownerId,
            currentUserRole: "org_admin",
            isCurrentOwner: false,
          });
          addToast({
            title: `Successfully transferred ownership.`,
            kind: "success",
          });
        },
        onError: (response: any) => {
          if (response.data.errors) {
            addToast({ title: response.data.errors[0], kind: "warning", delay: 10000 });
          }
        },
      },
    );
  };

  /* INVITES
  --===================================================-- */
  const updateInviteRole = (inviteId, invite) => {
    updateInvite(
      { id: inviteId, ...invite },
      {
        onSuccess: (data) => {
          addToast({
            title: `Updated role to ${userRoleNames(invite.role)}`,
            kind: "success",
          });
        },
        onError: (response: any) => {
          if (response.data.errors.general) {
            addToast({ title: response.data.errors.general[0], kind: "warning", delay: 10000 });
          }
        },
      },
    );
  };

  const handleResendInvite = (inviteId) => {
    resendInvite(
      { id: inviteId },
      {
        onSuccess: (data) => {
          addToast({ title: `Resent invitation to ${data.email}`, kind: "success" });
        },
        onError: (response: any) => {
          if (response.data.errors.general) {
            addToast({ title: response.data.errors.general[0], kind: "warning", delay: 10000 });
          }
        },
      },
    );
  };

  const handleDestroyInvite = (inviteId) => {
    destroyInvite(
      { id: inviteId },
      {
        onSuccess: (data) => {
          addToast({
            title: `Canceled invitation for ${data.email}`,
            kind: "success",
          });
        },
        onError: (response: any) => {
          if (response.data.errors.general) {
            addToast({ title: response.data.errors.general[0], kind: "warning", delay: 10000 });
          }
        },
      },
    );
  };

  const handleClickOnInvite = (e) => {
    e.preventDefault();

    trackEvent("invite_member_clicked");
    const result = checkGate();
    window.logger("%c[AccountTeam] handleClickOnInvite", "background-color: #7db4ec", {
      result,
    });

    if (!result.shouldProceed) {
      if (result.errorMessage) {
        // Handle error cases with user feedback
        addToast({
          title: result.errorMessage,
          kind: "warning",
          delay: 10000,
        });
        return;
      }
    }

    if (result.shouldProceed) {
      const modal = (
        <InviteModal
          headerTitleText="Invite team member"
          onCancel={removeModal}
          // onCompleteInvite={handleSubmitNewInviteForm}
        />
      );
      openModal(modal);
    } else if (result.modalType === "upgrade") {
      trackEvent("invite_member_upgrade_modal_shown", { current_plan: result.modalData!.currentPlanName! });
      const modal = (
        <UpgradeUserLimitModal
          onCancel={() => removeModal()}
          currentPlanName={result.modalData!.currentPlanName!}
          userLimit={result.modalData!.userLimit!}
          activeUserCount={result.modalData!.activeUserCount!}
          nextPlanName={result.modalData!.nextPlanName}
          hasPendingInvites={result.modalData!.hasPendingInvites}
          hasMultipleActiveUsers={result.modalData!.hasMultipleActiveUsers}
          location={props.location}
          history={props.history}
        />
      );
      openModal(modal);
    } else if (result.modalType === "freeTrial") {
      const modal = (
        <StartFreeTrialModal
          onCancel={() => removeModal()}
          purpose="addTeamMember"
          location={props.location}
        />
      );
      openModal(modal);
    } else if (result.modalType === "reactivate") {
      const modal = (
        <RestartSubscriptionUserLimitModal
          onCancel={() => removeModal()}
          location={props.location}
          history={props.history}
        />
      );
      openModal(modal);
    }
  };

  const InviteTeamMemberButton = currentUser.currentOrganizationUser.isAdmin ? (
    <Button
      type="button"
      styleType="secondary"
      onClick={handleClickOnInvite}
      isLoading={isLoadingUserGate}
    >
      Invite new member
    </Button>
  ) : null;

  const SidebarContent = (
    <Styled.Sidebar>
      <h3>Team roles</h3>
      <Styled.RoleContent>
        <h4>Owner</h4>
        <p>
          <b>The designated account owner with full administrative privileges.</b> The owner cannot
          be deactivated by any other user.
        </p>
      </Styled.RoleContent>
      <Styled.RoleContent>
        <h4>Admins</h4>
        <p>
          <b>Full administrative access to all jobs and team management.</b> Admins can create new
          jobs, invite team members, assign roles, and manage all hiring activities.
        </p>
      </Styled.RoleContent>
      <Styled.RoleContent>
        <h4>Members</h4>
        <p>
          <b>Standard team access with job-scoped editing permissions.</b> Members can view and edit
          their assigned jobs and manage candidates within them. Jobs are assigned by
          administrators.
        </p>
      </Styled.RoleContent>
      <Styled.RoleContent>
        <Styled.RoleHeader>
          <h4>Interviewers</h4>
        </Styled.RoleHeader>
        <p>
          <b>Focused role for dedicated interview staff.</b> Interviewers can be granted access to
          review specific candidates within a job, ensuring precise control over who reviews each
          applicant.
        </p>
      </Styled.RoleContent>
    </Styled.Sidebar>
  );

  return (
    <>
      <Helmet title="Team" />
      <SettingsContainer
        title="Team management"
        description="This section is for managing who from your organization can log in and use your Polymer account."
        sidebar={SidebarContent}
      >
        <FormSection>
          <Styled.Team data-testid="team-members">
            {memberNodes()}
            {inviteNodes()}
          </Styled.Team>
          {InviteTeamMemberButton}
        </FormSection>
      </SettingsContainer>
    </>
  );
}

export default AccountTeam;

/* Styled Components
======================================================= */
const Styled: any = {};

Styled.Team = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AccountTeam;
    ${[t.my(8), t.rounded.md]}
    border: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
    background-color: ${t.dark ? t.color.gray[800] : t.color.white};
  `;
});

Styled.Sidebar = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AccountTeam_Sidebar;
    ${t.px(4)};
    position: sticky;
    top: ${t.spacing[4]};
    h3 {
      ${[t.mt(1), t.text.bold]};
      color: ${t.dark ? t.color.gray[300] : t.color.black};
      font-size: 1rem;
    }
  `;
});

Styled.RoleContent = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AccountTeam_RoleContent;
    ${t.mt(5)};
    display: flex;
    flex-direction: column;
    h4 {
      ${[t.text.bold]};
      color: ${t.dark ? t.color.gray[300] : t.color.gray[900]};
      font-size: 0.875rem;
    }
    p {
      ${[t.text.sm, t.mt(2)]};
      line-height: 1.4;
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    }
    b {
      ${[t.text.semibold]};
      color: ${t.dark ? t.color.gray[300] : t.color.gray[700]};
    }
  `;
});

Styled.RoleHeader = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AccountTeam_RoleHeader;
    display: flex;
    align-items: center;
  `;
});
